import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  Divider,
  Tooltip,
  Paper,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("es-AR");
  } catch {
    return String(v);
  }
};

const safe = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ py: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 120 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, wordBreak: "break-word" }}
      >
        {safe(value)}
      </Typography>
    </Stack>
  );
}

function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export function ReportDetailDialog({
  open,
  title,
  subtitle,
  payload,
  onClose,
}) {

  const report = payload; // conserva reportType, reportId, postId, commentId
  const d = payload?.data?.data; // detalle del post/comment
  const author = d?.author;

  const reporterName = author
    ? `${author?.name ?? ""} ${author?.lastname ?? ""}`.trim()
    : "—";

  const contentType = report?.reportType ?? "—";

  // El contenido reportado en eco es el post/comment
  const contentId =
    report?.reportType === "Post"
      ? report?.postId ?? d?.id
      : report?.commentId ?? d?.id;

  const text = d?.text ?? "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "action.hover",
              }}
            >
              <ArticleRoundedIcon />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                {title || "Detalle del reporte"}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.5, flexWrap: "wrap" }}
              >
                {subtitle ? (
                  <Chip
                    size="small"
                    label={subtitle}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}

                {report?.reportType ? (
                  <Chip
                    size="small"
                    label={`Tipo: ${report.reportType}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}

                {d?.createdAt ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(d.createdAt)}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" gap={0.5}>
            <Tooltip title="Cerrar">
              <IconButton onClick={onClose} size="small">
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {!d ? (
          <Typography color="text.secondary">No hay detalle para mostrar.</Typography>
        ) : (
          <Stack spacing={2}>
            <Section title="Resumen">
              <InfoRow label="ID del reporte" value={report?.reportId ?? "—"} />
              <InfoRow label="Tipo de reporte" value={contentType} />
              <InfoRow label="Fecha" value={formatDate(d?.createdAt)} />
            </Section>

            <Section title="Autor del contenido">
              <InfoRow label="Nombre" value={reporterName || "—"} />
              <InfoRow label="Email" value={author?.email ?? "—"} />
              <InfoRow label="ID" value={author?.id ?? d?.authorId ?? "—"} />
            </Section>

            <Section title="Contenido reportado">
              <InfoRow label="Tipo de contenido" value={contentType} />
              <InfoRow label="ID del contenido" value={contentId ?? "—"} />
              <InfoRow label="Fecha de creacion" value={formatDate(d?.createdAt) ?? "—"} />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Texto
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {safe(text)}
                </Typography>
              </Paper>
            </Section>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>

        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
